<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 style="grid-area: header" id="microarray-title">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="microarray-test-grid"
      >
        <p style="grid-area: microarrayInfo">
          A
          <a
            href="https://www.vcgs.org.au/tests/paediatric-microarray"
            target="_blank"
            >chromosome microarray</a
          >
          is a powerful tool used to look at sub microscopic changes in our
          genetic information that may impact health or development. Microarray
          is considered the gold standard test for the investigation of
          intellectual disability and developmental and behavioural concerns
          such as autism spectrum disorders and congenital malformations.
        </p>
        <v-checkbox
          v-model="urgentKit"
          label="Urgent kit required"
          :id="testId + '.urgent'"
          class="mb-3"
          v-if="patientdata.sampleType === 'SALIVA'"
        ></v-checkbox>
        <v-row v-if="urgentKit">
          <v-col class="custom-col">
            <span class="text-title">Urgent kit reason</span>
            <v-text-field
              v-model="urgentReason"
              :id="testId + '.urgentReason'"
              outlined
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="my-2">
            <v-col cols="12">
              <span class="text-title">Is the patient pregnant?*</span>
              <v-select
                v-model="pregnant"     
                :items="pregnancyOptions"     
                item-text="key"               
                item-value="value"             
                :id="testId + '.pregnant'"
                :rules="ruleNotNull"
                outlined
                hide-details="auto"
              />
            </v-col>
          </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Family member previously tested?</span>
            <v-select
              v-model="previousTest"
              :items="yesNoOptions"
              item-text="key"
              item-value="value"
              :id="testId + '.familyPrevTested'"
              outlined
              hide-details="auto"
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="previousTest === true">
          <v-col class="custom-col">
            <span class="text-title">They were tested at VCGS</span>
            <v-select
              v-model="previouslyTested"
              :items="yesNoOptions"
              item-text="key"
              item-value="value"
              :id="testId + '.previouslyTestedVcgs'"
              outlined
              hide-details="auto"
            ></v-select>
          </v-col>
          <v-col class="custom-col" v-if="previouslyTested === true">
            <span class="text-title">VCGS Sample ID</span>
            <v-text-field
              v-model="posCtrlSampleId"
              :id="testId + '.posCtrlSampleId'"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="text-hint"
              >e.g. 20C100123. This is located in the top right section of our
              reports. If you cannot find this please provide the name and date
              of birth of the person previously tested.</span
            >
          </v-col>
          <v-col class="custom-col" v-if="previouslyTested === true">
            <span class="text-title">Relationship to person being tested</span>
            <v-text-field
              v-model="rhipToPosCtrl"
              :id="testId + '.rhipToPosCtrl'"
              hint="e.g. sibling, parent, aunt, etc."
              outlined
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>

        <SendEmailAlert
          class="mt-6"
          v-if="previouslyTested === false && previousTest"
        />
        <div v-if="previouslyTested === true && previousTest === true">
          <v-row>
            <v-col class="custom-col">
              <span class="text-title"
                >Is the person being tested affected by the same
                condition?</span
              >

              <v-text-field
                v-model="relationAffected"
                :id="testId + '.relationAffected'"
                outlined
                hint="e.g. mildly affected"
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>
        </div>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Clinical notes</span>
            <v-textarea
              v-model="clinicalNotes"
              :id="testId + '.clinicalNotes'"
              hint="e.g. clinical indication for testing, relevant family history, results of relevant prior genetic and non-genetic investigations, biochemistry, histopathology and imaging, etc."
              outlined
            ></v-textarea>
          </v-col>
        </v-row>
        <TestPriceAlert :price="price" :medicare="medicare" />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        id="microarray_next_section"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FormRulesMixin from "../../mixins/FormRulesMixin";
import DropDownMixin from "../../mixins/DropDownMixin";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceAlert from "../common/TestPriceAlert.vue";
import SendEmailAlert from "../common/SendEmailAlert.vue";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import { EventBus } from "@/event-bus";
import { computed, watch } from 'vue'; 

export default {
  name: "",
  mixins: [FormRulesMixin, DropDownMixin, RetrieveJSONMixin],
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
  },
  components: { TestPriceAlert, SendEmailAlert },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.simpleTestPricing = await this.execRetrieveJson(
      "json/pricing/simple.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    const element = document.getElementById("microarray-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  },
  data: () => ({
    simpleTestPricing: null,
    urgentKit: false,
    urgentReason: "",
    previousTest: null,
    previouslyTested: null,
    relationAffected: "",
    rhipToPosCtrl: "",
    yesNoOptions: [
      { key: "Yes", value: true },
      { key: "No", value: false },
    ],
    posCtrlSampleId: "",
    clinicalNotes: "",
    pregnancyOptions: [
      { key: "Yes", value: "YES" },
      { key: "No", value: "NO" }
    ],
  }),
  computed: {
    getPriceDetails() {
      if (!this.simpleTestPricing) return null;
      return this.simpleTestPricing[this.testId];
    },
    price() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.price;
      } else {
        return null;
      }
    },
    testCode() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.testCode;
      } else {
        return "";
      }
    },
    medicare() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails !== null) {
        return priceDetails.medicare;
      } else {
        return null;
      }
    },
  },
  setup() {
    const store = useSectionStore()
    const pregnant = computed({
      get: () => store.pregnant,
      set: val => { store.pregnant = val },
    })
    watch(pregnant, val => { store.pregnant = val })
    return { pregnant }
  },
  methods: {
    transferData: function() {
      return {
        testType: this.testId,
        urgent: this.urgentKit,
        urgentReason: this.urgentReason,
        familyPrevTested: this.previouslyTested,
        posCtrlSampleId: this.posCtrlSampleId,
        relationAffected: this.relationAffected,
        clinicalNotes: this.clinicalNotes,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
        rhipToPosCtrl: this.rhipToPosCtrl,
      };
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
  },
};
</script>
<style scoped></style>
